import React, { useState } from 'react';
import './DiagonalCollage.css';

// Diagonally-sliced image collage where hovering (or focusing) a panel
// expands it and dims/shrinks the rest; every panel links to the same
// vertical's main page (not individual pieces) — a whole vertical's
// collage shares one href. Diagonal edges are cut with clip-path per
// panel, then pulled together with negative margins so the cuts
// interlock instead of leaving gaps. Panels with no image uploaded
// render as an inert placeholder so the collage always keeps its
// 4-panel shape.
const DIAGONAL = '3.5vw';

const clipPathFor = (index, count) => {
  if (index === 0) return `polygon(0 0, 100% 0, calc(100% - ${DIAGONAL}) 100%, 0 100%)`;
  if (index === count - 1) return `polygon(${DIAGONAL} 0, 100% 0, 100% 100%, 0 100%)`;
  return `polygon(${DIAGONAL} 0, 100% 0, calc(100% - ${DIAGONAL}) 100%, 0 100%)`;
};

export const DiagonalCollage = ({ panels = [] }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <div className="diagonal-collage" role="list">
      {panels.map((panel, index) => {
        const isHovered = hoveredIndex === index;
        const isDimmed = hoveredIndex !== null && !isHovered;
        const isEmpty = !panel;
        const isClickable = !!panel?.href;

        const style = {
          clipPath: clipPathFor(index, panels.length),
          marginLeft: index === 0 ? 0 : `calc(-1 * ${DIAGONAL})`,
          flexGrow: isHovered ? 2.4 : isDimmed ? 0.7 : 1,
          // Quoted so filenames with spaces/parentheses (browsers don't
          // urlencode uploaded filenames in the Active Storage path) don't
          // break unquoted CSS url() syntax and silently drop the property.
          backgroundImage: panel ? `url("${panel.image}")` : undefined
        };
        const className = `diagonal-collage-panel${isHovered ? ' is-hovered' : ''}${isDimmed ? ' is-dimmed' : ''}${isEmpty ? ' is-empty' : ''}`;

        if (isEmpty) {
          return (
            <div key={index} role="listitem" aria-hidden="true" className={className} style={style}>
              <span className="diagonal-collage-overlay" aria-hidden="true" />
            </div>
          );
        }

        const handlers = {
          onMouseEnter: () => setHoveredIndex(index),
          onMouseLeave: () => setHoveredIndex(null),
          onFocus: () => setHoveredIndex(index),
          onBlur: () => setHoveredIndex(null)
        };
        const label = (
          <>
            <span className="diagonal-collage-overlay" aria-hidden="true" />
            {panel.title && (
              <span className="diagonal-collage-label" style={{ opacity: isHovered ? 1 : 0 }}>
                <span className="diagonal-collage-title">{panel.title}</span>
              </span>
            )}
          </>
        );

        if (isClickable) {
          return (
            <a key={index} href={panel.href} role="listitem" aria-label={panel.title} className={className} style={style} {...handlers}>
              {label}
            </a>
          );
        }

        return (
          <div key={index} role="listitem" aria-label={panel.title} className={className} style={style} {...handlers}>
            {label}
          </div>
        );
      })}
    </div>
  );
};

export default DiagonalCollage;
